import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PrayerVoice, PRAYER_VOICES } from "@/hooks/prayer/usePrayerVoices";
import { Loader2, Mic, Volume2 } from "lucide-react";

interface PrayerGeneratorFormProps {
  prayerRequest: string;
  onPrayerRequestChange: (value: string) => void;
  selectedVoice: PrayerVoice;
  onVoiceChange: (voice: PrayerVoice) => void;
  onSubmit: () => void;
  loading: boolean;
}

const PrayerGeneratorForm: React.FC<PrayerGeneratorFormProps> = ({
  prayerRequest,
  onPrayerRequestChange,
  selectedVoice,
  onVoiceChange,
  onSubmit,
  loading,
}) => {
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlayPreview = (voiceId: string, previewUrl: string) => {
    if (playingAudio === voiceId) {
      // Stop current audio if it's playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPlayingAudio(null);
    } else {
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      
      // Play the new audio
      setPlayingAudio(voiceId);
      audioRef.current = new Audio(previewUrl);
      audioRef.current.onended = () => setPlayingAudio(null);
      audioRef.current.play().catch(error => {
        console.error("Error playing audio:", error);
        setPlayingAudio(null);
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-selah-gold/10">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-selah-navy mb-4 italic">
          What would you like to pray about?
        </h1>
        <p className="text-sm text-selah-navy italic mb-3">
          <span className="font-medium text-selah-gold">Tip:</span> For a more personal prayer, please include your name and the names of anyone you're praying for or with.
        </p>
        <Textarea
          id="prayer-request"
          placeholder="Share your heart, struggles, or praises..."
          value={prayerRequest}
          onChange={(e) => onPrayerRequestChange(e.target.value)}
          className="min-h-[120px] text-base bg-heaven-white border-selah-gold/20 focus:border-selah-gold focus:ring-selah-gold/20"
          disabled={loading}
        />
      </div>

      <div className="mb-6">
        <Label className="text-lg font-semibold text-selah-navy mb-2">
          Choose a Voice Companion
        </Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
          {["David", "James", "Solomon", "Sarah", "Elizabeth", "Ruth"].map((voiceName) => {
            const voice = PRAYER_VOICES.find(v => v.name === voiceName);
            if (!voice) return null;

            const isSelected = selectedVoice.id === voice.id;
            const isPlaying = playingAudio === voice.id;
            
            return (
              <div key={voice.id} className="flex flex-col">
                <Button
                  type="button"
                  className={`flex flex-col items-center justify-center p-4 h-auto mb-2 border-2
                    ${isSelected 
                      ? "bg-[#34495E] text-white border-[#D4AF37]" 
                      : "bg-white text-[#34495E] border-[#D4AF37] hover:bg-[#34495E]/5"
                    }`}
                  onClick={() => onVoiceChange(voice)}
                  disabled={loading}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 
                    ${isSelected 
                      ? "bg-[#D4AF37] text-white" 
                      : "bg-white text-[#D4AF37] border border-[#D4AF37]"
                    }`}>
                    <Mic size={18} />
                  </div>
                  <span className="font-medium">{voice.name}</span>
                  <span className="text-[11px] md:text-xs mt-1 text-center">
                    {voice.description}
                  </span>
                </Button>
                
                <Button 
                  type="button" 
                  size="sm" 
                  className={`text-xs flex items-center justify-center gap-1 py-1 border-0
                    ${isPlaying 
                      ? "bg-[#34495E] text-white" 
                      : "bg-transparent text-[#34495E] hover:bg-[#34495E]/5"
                    }`}
                  onClick={() => handlePlayPreview(voice.id, voice.previewUrl)}
                  disabled={loading}
                >
                  <Volume2 size={14} className={isPlaying ? "animate-pulse" : ""} />
                  {isPlaying ? "Stop Preview" : "Preview Voice"}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex justify-center">
        <Button
          onClick={onSubmit}
          disabled={loading}
          className={`font-medium py-2 px-8 rounded-md text-lg min-w-[200px] 
            bg-[#34495E] text-white hover:bg-[#D4AF37] hover:text-[#34495E]
            ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Generating Prayer...
            </>
          ) : (
            "Receive Your Prayer"
          )}
        </Button>
      </div>
    </div>
  );
};

export default PrayerGeneratorForm;
